import React, { useState } from 'react';
import axios from 'axios';

import CardDialog from '~/components/CardDialog';
import AlertBanner from '~/components/core/AlertBanner';
import Button from '~/components/core/Atomic/Buttons/Button';
import MenuItem from '~/components/core/Atomic/MenuItem';
import SingleSelectField from '~/components/core/Molecules/Fields/SingleSelectField';
import TextField from '~/components/core/Molecules/Fields/TextField';
import type { PromotionPreviewResult } from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

import PromotionChangeSelector from './PromotionChangeSelector';
import PromotionEnvBadge from './PromotionEnvBadge';
import PromotionExportBlockedBanner from './PromotionExportBlockedBanner';
import PromotionNewMembersStep from './PromotionNewMembersStep';
import PromotionReasonsNote from './PromotionReasonsNote';
import type { PromotionDanglingReferences, PromotionExportBlocked } from './promotionsUtils';
import {
  getPromotionDanglingReferences,
  getPromotionExportBlocked,
  getPromotionImmutableFieldChangedMessage,
  isPendingPromotionExistsError,
  isTargetUnreachableError,
  PENDING_PROMOTION_EXISTS_MESSAGE,
  PROMOTION_DANGLING_REFERENCE_TITLE,
  PROMOTION_IMMUTABLE_FIELD_CHANGED_TITLE,
  promotionsBaseRoute,
  TARGET_UNREACHABLE_MESSAGE,
} from './promotionsUtils';
import type { OutgoingPromotionRow, Pairing, PromotionBlockedSection, PromotionChange } from './types';
import { PROMOTION_STATUS } from './types';
import { useNewMemberStep } from './useNewMemberStep';

interface CreatePromotionDialogProps {
  open: boolean;
  organizationId: number;
  sourcePairings: Pairing[];
  /** The source's already-loaded outgoing requests; used to preempt a duplicate before Submit. */
  pendingOutgoing?: OutgoingPromotionRow[];
  onClose: () => void;
  onCreated: () => void;
  /** Open the given outgoing promotion's detail (owned by the parent tab). */
  onViewPendingPromotion?: (requestUuid: string) => void;
}

/** The "Promote to" option content: the target org, the target-environment badge (only for cross-deployment
 * pairings — the backend blanks target_deployment_id for same-DB/upload pairings, so presence of a value is
 * the single cross-deployment signal), and a pending-review suffix. Rendered through the shared
 * {@link PromotionEnvBadge}, like every other env render site, so the friendly label shows consistently. */
const pairingOptionLabel = (pairing: Pairing, hasPending: boolean): React.ReactNode => (
  <>
    {pairing.target_org_external_id}
    {pairing.target_deployment_id ? (
      <>
        {' '}
        <PromotionEnvBadge subDomain={pairing.target_deployment_id} />
      </>
    ) : null}
    {hasPending ? ' — pending review' : ''}
  </>
);

/** Source-side create flow: pick a target pairing, preview the change list, then submit the promotion
 * scoped to the selected subset of changes (defaults to all selected, so selecting everything is
 * equivalent to a full promotion). The preview-result → change-list → select/submit body is the shared
 * {@link PromotionChangeSelector}, reused by the uploaded-artifact create flow. */
const CreatePromotionDialog: React.FC<CreatePromotionDialogProps> = ({
  open,
  organizationId,
  sourcePairings,
  pendingOutgoing = [],
  onClose,
  onCreated,
  onViewPendingPromotion,
}) => {
  const [pairingId, setPairingId] = useState<number | ''>('');
  const [preview, setPreview] = useState<PromotionPreviewResult | null>(null);
  const [selectedChangeIds, setSelectedChangeIds] = useState<string[]>([]);
  // The required, human-readable promotion name captured at create time. `isNameDirty` gates the inline
  // "required" error so it shows only after the user has touched the field, not on first open.
  const [requestName, setRequestName] = useState('');
  const [isNameDirty, setIsNameDirty] = useState(false);
  // True while one or more selected changes are hidden by the change selector's active scope filters — Submit
  // is blocked until the user clears the filters or deselects the hidden changes (reported by the selector).
  const [submitBlocked, setSubmitBlocked] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // The cross-deployment target was unreachable (502) on preview/submit: non-blocking; the Preview /
  // Submit buttons are the retry. Not a bug → no error modal / Sentry.
  const [targetUnreachable, setTargetUnreachable] = useState(false);
  // A pending-exists 409 came back from a target we couldn't see locally (different source org, or a
  // cross-deployment target in another DB). Inline + non-destructive: keep the preview so the user can
  // resubmit once the slot clears.
  const [pendingExists, setPendingExists] = useState(false);
  // An older cross-deployment target that cannot leave a blocked section out still refuses the whole preview
  // (422 PROMOTION_EXPORT_BLOCKED) — operator-fixable, listed per offender in-dialog rather than through the
  // error modal / Sentry.
  const [exportBlocked, setExportBlocked] = useState<PromotionExportBlocked | null>(null);
  // The source sets a field a promotion can't change differently from the target (422
  // PROMOTION_IMMUTABLE_FIELD_CHANGED) — operator-fixable, shown in-dialog rather than through the error modal /
  // Sentry.
  const [immutableFieldChanged, setImmutableFieldChanged] = useState<string | null>(null);
  // Changes of the preview name items the target doesn't have and the promotion doesn't create (422
  // PROMOTION_DANGLING_REFERENCE) — operator-fixable, listed per reference in-dialog rather than through the error
  // modal.
  const [danglingReferences, setDanglingReferences] = useState<PromotionDanglingReferences | null>(null);
  // Sections the preview left out because one side's export of them is blocked. Taken from the initial
  // preview only: a refine re-diffs the same pinned exports and does not re-send the reasons.
  const [blockedSections, setBlockedSections] = useState<PromotionBlockedSection[]>([]);

  // Clear the stale "unreachable" notice when the target changes or the dialog reopens — the dialog
  // stays mounted, so the flag would otherwise linger onto a different target / a fresh open.
  React.useEffect(() => {
    setTargetUnreachable(false);
    setPendingExists(false);
    setExportBlocked(null);
    setImmutableFieldChanged(null);
    setDanglingReferences(null);
    setBlockedSections([]);
  }, [pairingId, open]);

  const baseRoute = promotionsBaseRoute(organizationId);

  // Shared interim "New members" step (opt-in + refine), identical to the uploaded-artifact create flow.
  const memberStep = useNewMemberStep({
    preview,
    setPreview,
    baseRoute,
    resetSelection: () => {
      setSelectedChangeIds([]);
      setSubmitBlocked(false);
    },
  });

  // The pending outgoing request (if any) occupying this pairing's target slot. A pending promotion is
  // keyed by target org + deployment, so the match is on all three fields. Shared by the dropdown
  // annotation and the Submit-disable / warning-banner logic so the two can't drift apart.
  const findPendingForPairing = (pairing: Pairing) =>
    pendingOutgoing.find(
      (o) =>
        o.cached_status === PROMOTION_STATUS.PENDING &&
        o.target_org_external_id === pairing.target_org_external_id &&
        o.target_deployment_id === pairing.target_deployment_id
    );

  const selectedPairing = sourcePairings.find((p) => p.id === pairingId);
  const pendingForTarget = selectedPairing ? findPendingForPairing(selectedPairing) : undefined;

  const resetAndClose = () => {
    setPairingId('');
    setPreview(null);
    setSelectedChangeIds([]);
    setSubmitBlocked(false);
    setRequestName('');
    setIsNameDirty(false);
    setBlockedSections([]);
    memberStep.resetMembers();
    onClose();
  };

  const handlePreview = async () => {
    if (pairingId === '') {
      return;
    }
    try {
      setIsPreviewing(true);
      setPreview(null);
      // Drop the prior preview's selection so the footer Submit can't stay enabled from a stale subset
      // during the paint before the selector re-reports the new preview's full selection.
      setSelectedChangeIds([]);
      setSubmitBlocked(false);
      setTargetUnreachable(false);
      setPendingExists(false);
      setExportBlocked(null);
      setImmutableFieldChanged(null);
      setDanglingReferences(null);
      setBlockedSections([]);
      const { data } = await axios.post<PromotionPreviewResult>(`${baseRoute}/preview`, { pairing_id: pairingId });
      setPreview(data);
      setBlockedSections(data.blocked_sections ?? []);
      memberStep.seedPromotedMembers(data);
    } catch (error) {
      const blocked = getPromotionExportBlocked(error);
      const dangling = getPromotionDanglingReferences(error);
      const immutableFieldMessage = getPromotionImmutableFieldChangedMessage(error);
      if (blocked) {
        setExportBlocked(blocked);
      } else if (dangling) {
        setDanglingReferences(dangling);
      } else if (immutableFieldMessage) {
        setImmutableFieldChanged(immutableFieldMessage);
      } else if (isTargetUnreachableError(error)) {
        setTargetUnreachable(true);
      } else {
        await reportAxiosError(error);
      }
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleSubmit = async () => {
    if (pairingId === '' || !preview || selectedChangeIds.length === 0 || !requestName.trim()) {
      return;
    }
    try {
      setIsSubmitting(true);
      setTargetUnreachable(false);
      setPendingExists(false);
      await axios.post(baseRoute, {
        pairing_id: pairingId,
        preview_token: preview.preview_token,
        selected_change_ids: selectedChangeIds,
        request_name: requestName.trim(),
      });
      onCreated();
      resetAndClose();
    } catch (error) {
      if (isTargetUnreachableError(error)) {
        // Target unreachable — keep the preview so the user can retry Submit once it's back.
        setTargetUnreachable(true);
      } else if (isPendingPromotionExistsError(error)) {
        // A pending request already occupies this target's slot (one we couldn't see locally). Keep the
        // preview/selection so the user can resubmit once it clears; no error modal / Sentry.
        setPendingExists(true);
      } else {
        // Genuine preview drift/expiry/stale selection (409) or other error — re-preview to refresh.
        await reportAxiosError(error);
        setPreview(null);
        setBlockedSections([]);
        setSelectedChangeIds([]);
        setSubmitBlocked(false);
        memberStep.resetMembers();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const { showMemberStep, showChangeSelector } = memberStep;

  return (
    <CardDialog
      isDialog
      title="New promotion"
      open={open}
      onClose={resetAndClose}
      preventClose={isSubmitting || memberStep.isRefining}
      fullWidth
      maxWidth="md"
      footerActions={
        <div className="flex items-center gap-x-12">
          <Button variant="text" onClick={resetAndClose}>
            Cancel
          </Button>
          {!preview ? (
            <Button
              variant="contained"
              color="primary"
              disabled={pairingId === '' || isPreviewing}
              onClick={handlePreview}
            >
              {isPreviewing ? 'Previewing…' : 'Preview changes'}
            </Button>
          ) : showMemberStep ? (
            <Button
              variant="contained"
              color="primary"
              disabled={memberStep.isRefining}
              onClick={memberStep.handleConfirmMembers}
            >
              {memberStep.isRefining ? 'Loading…' : 'Continue to review'}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              disabled={
                selectedChangeIds.length === 0 ||
                isSubmitting ||
                Boolean(pendingForTarget) ||
                submitBlocked ||
                !requestName.trim()
              }
              onClick={handleSubmit}
            >
              {isSubmitting ? 'Submitting…' : 'Submit promotion'}
            </Button>
          )}
        </div>
      }
    >
      <div className="flex flex-col gap-16 py-8">
        <SingleSelectField<number | ''>
          label="Promote to"
          value={pairingId}
          fullWidthField
          disabled={Boolean(preview)}
          onChange={(value) => {
            setPairingId(typeof value === 'number' ? value : '');
            setPreview(null);
            setSelectedChangeIds([]);
          }}
        >
          {sourcePairings.map((pairing) => (
            <MenuItem key={pairing.id} value={pairing.id}>
              {pairingOptionLabel(pairing, Boolean(findPendingForPairing(pairing)))}
            </MenuItem>
          ))}
        </SingleSelectField>

        {/* Two-step flow: first screen picks the target; the required Name is only asked for once the
            preview (the change list) has rendered, at which point the target is locked so it can't drift
            out from under a captured name/selection. */}
        {showChangeSelector && (
          <TextField
            label="Name"
            size="medium"
            value={requestName}
            onChange={(newValue: string) => {
              setRequestName(newValue);
              setIsNameDirty(true);
            }}
            error={isNameDirty && !requestName.trim()}
            helperText={isNameDirty && !requestName.trim() ? 'A name is required' : ''}
            fullWidth
            inputProps={{ maxLength: 256 }}
          />
        )}

        {pendingForTarget && (
          <AlertBanner
            withIcon
            alertType={AlertBanner.ALERT_TYPES.WARNING}
            title="Promotion already pending for this target"
            note={
              <div className="flex flex-wrap items-center gap-x-12 gap-y-8">
                <span>{PENDING_PROMOTION_EXISTS_MESSAGE}</span>
                {onViewPendingPromotion && (
                  <Button variant="outlined" onClick={() => onViewPendingPromotion(pendingForTarget.request_uuid)}>
                    View pending promotion
                  </Button>
                )}
              </div>
            }
          />
        )}

        {targetUnreachable && (
          <AlertBanner
            withIcon
            alertType={AlertBanner.ALERT_TYPES.WARNING}
            title="Target environment unreachable"
            note={TARGET_UNREACHABLE_MESSAGE}
          />
        )}

        {exportBlocked && <PromotionExportBlockedBanner exportBlocked={exportBlocked} />}

        {immutableFieldChanged && (
          <AlertBanner
            withIcon
            alertType={AlertBanner.ALERT_TYPES.ERROR}
            title={PROMOTION_IMMUTABLE_FIELD_CHANGED_TITLE}
            note={immutableFieldChanged}
          />
        )}

        {danglingReferences && (
          <AlertBanner
            withIcon
            alertType={AlertBanner.ALERT_TYPES.ERROR}
            title={PROMOTION_DANGLING_REFERENCE_TITLE}
            note={<PromotionReasonsNote message={danglingReferences.message} reasons={danglingReferences.reasons} />}
          />
        )}

        {pendingExists && (
          <AlertBanner
            withIcon
            alertType={AlertBanner.ALERT_TYPES.WARNING}
            title="Promotion already pending for this target"
            note={PENDING_PROMOTION_EXISTS_MESSAGE}
          />
        )}

        {showMemberStep && preview && (
          <PromotionNewMembersStep
            newMembers={preview.new_members ?? { lobs: [], sub_organizations: [] }}
            changes={(preview.changes ?? []) as PromotionChange[]}
            targetName={selectedPairing?.target_org_external_id}
            promotedLobIds={memberStep.promotedLobIds}
            promotedSubOrgIds={memberStep.promotedSubOrgIds}
            onToggleMember={memberStep.handleToggleMember}
          />
        )}

        {showChangeSelector && preview && (
          <PromotionChangeSelector
            previewResult={preview}
            onSelectionChange={setSelectedChangeIds}
            onSubmitBlockedChange={setSubmitBlocked}
            promotedLobIds={memberStep.promotedLobIds}
            promotedSubOrgIds={memberStep.promotedSubOrgIds}
            blockedSections={blockedSections}
          />
        )}
      </div>
    </CardDialog>
  );
};

export default CreatePromotionDialog;
